import type {
  CreateInstallmentBody,
  CreateProposalBody,
  DealDetailResource,
  DealInstallmentResource,
  DealProposalResource,
  PlannedInstallmentBody,
  ProposalStatusValue,
  UpdateInstallmentBody,
} from '@majuscule/contracts';
import { useMutation } from '@tanstack/react-query';
import { FileText, Plus, Trash2 } from 'lucide-react';
import { type JSX, useState } from 'react';
import {
  crmSend,
  crmSendEmpty,
  errorMessage,
  euroInputError,
  euroInputOf,
  eurOrDash,
  formatDate,
  INSTALLMENT_STATUS_LABEL,
  parisToday,
  parseEuroInput,
  PROPOSAL_STATUS_LABEL,
} from '../../lib/crm';
import { openDealDocument } from './documents';
import { MutationError, useCrmRefresh } from './shared';
import { Badge, type BadgeVariant } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHead } from '@/components/ui/card';
import { Dialog, DialogBody, DialogContent, DialogFooter, DialogHead } from '@/components/ui/dialog';
import { EmptyState } from '@/components/ui/empty';
import { Field, Fields2, Fieldset, InputGroup } from '@/components/ui/field';
import { DateInput } from '@/components/ui/date-input';
import { Input } from '@/components/ui/input';
import { KvList, KvRow } from '@/components/ui/kv';
import { NativeSelect } from '@/components/ui/native-select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';

const PROPOSAL_BADGE: Record<ProposalStatusValue, BadgeVariant> = {
  draft: 'neutral',
  sent: 'info',
  accepted: 'success',
  declined: 'danger',
  superseded: 'neutral',
};

const INSTALLMENT_BADGE: Record<DealInstallmentResource['status'], BadgeVariant> = {
  planned: 'neutral',
  invoiced: 'warning',
  paid: 'success',
};

function useDialog<T>(): [T | null, (value: T | null) => void] {
  const [value, setValue] = useState<T | null>(null);
  return [value, setValue];
}

// --- Signature et acomptes --------------------------------------------------------

/**
 * Signature (mise en place) et ses acomptes (lot 21) : prévus, facturés, payés,
 * suivis à la main. Reste à planifier = signature − acomptes.
 */
export function SignatureCard({ detail }: { detail: DealDetailResource }): JSX.Element {
  const refresh = useCrmRefresh();
  const [editing, setEditing] = useDialog<DealInstallmentResource | 'new'>();
  const [statusOf, setStatusOf] = useDialog<DealInstallmentResource>();
  const { signature, installments, deal } = detail;
  const closed = deal.stage === 'lost';
  const remove = useMutation({
    mutationFn: (id: string) => crmSendEmpty('DELETE', `/installments/${id}`),
    onSuccess: () => {
      refresh();
    },
  });
  const unscheduled = signature.unscheduled === null ? null : Number(signature.unscheduled);

  return (
    <Card>
      <CardHead
        title="Signature et acomptes"
        description="Mise en place, payée en un ou plusieurs acomptes (HT)"
        actions={
          closed ? undefined : (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                setEditing('new');
              }}
            >
              <Plus aria-hidden="true" />
              Acompte
            </Button>
          )
        }
      />
      <CardContent className="grid gap-4">
        <KvList>
          <KvRow label="Signature" mono>
            {signature.total === null ? 'À chiffrer' : eurOrDash(signature.total)}
          </KvRow>
          <KvRow label="Facturé" mono>
            {eurOrDash(signature.invoiced)}
          </KvRow>
          <KvRow label="Encaissé" mono>
            <span className="font-semibold">{eurOrDash(signature.paid)}</span>
          </KvRow>
          <KvRow label="Facturé, en attente" mono>
            {eurOrDash(signature.outstanding)}
          </KvRow>
        </KvList>
        {unscheduled !== null && unscheduled !== 0 ? (
          <p className="m-0 text-caption text-muted-foreground">
            {unscheduled > 0
              ? `Reste ${eurOrDash(signature.unscheduled)} à répartir en acomptes.`
              : `Les acomptes dépassent la signature de ${eurOrDash(String(-unscheduled))}.`}
          </p>
        ) : null}
        {installments.length === 0 ? (
          <EmptyState
            className="py-2"
            title="Aucun acompte"
            description="Ajouter les échéances, ou accepter une proposition qui en prévoit."
          />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Acompte</TableHead>
                <TableHead kind="num">Montant</TableHead>
                <TableHead kind="mono">Échéance</TableHead>
                <TableHead>Statut</TableHead>
                <TableHead kind="act">
                  <span className="sr-only">Actions</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {installments.map((installment) => (
                <TableRow key={installment.id}>
                  <TableCell kind="name">{installment.label}</TableCell>
                  <TableCell kind="num">{eurOrDash(installment.amount)}</TableCell>
                  <TableCell kind="mono">
                    {installment.dueDate === null ? '—' : formatDate(installment.dueDate)}
                    {installment.late ? <span className="ml-1 font-sans text-destructive">en retard</span> : null}
                  </TableCell>
                  <TableCell>
                    <Badge variant={INSTALLMENT_BADGE[installment.status]}>
                      {INSTALLMENT_STATUS_LABEL[installment.status]}
                    </Badge>
                    {installment.status === 'paid' && installment.paidOn !== null ? (
                      <span className="ml-1 text-caption text-muted-foreground">
                        le {formatDate(installment.paidOn)}
                      </span>
                    ) : null}
                  </TableCell>
                  <TableCell kind="act">
                    {closed ? null : (
                      <div className="flex justify-end gap-1">
                        <Button
                          type="button"
                          variant="outline"
                          size="xs"
                          onClick={() => {
                            setStatusOf(installment);
                          }}
                        >
                          {installment.status === 'planned'
                            ? 'Facturer'
                            : installment.status === 'invoiced'
                              ? 'Encaisser'
                              : 'Statut'}
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="xs"
                          onClick={() => {
                            setEditing(installment);
                          }}
                        >
                          Modifier
                        </Button>
                        {installment.status === 'planned' ? (
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon-sm"
                            aria-label={`Supprimer ${installment.label}`}
                            disabled={remove.isPending}
                            onClick={() => {
                              remove.mutate(installment.id);
                            }}
                          >
                            <Trash2 aria-hidden="true" />
                          </Button>
                        ) : null}
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        <MutationError message={remove.isError ? errorMessage(remove.error, 'Suppression refusée.') : null} />
      </CardContent>
      <Dialog
        open={editing !== null}
        onOpenChange={(next) => {
          if (!next) setEditing(null);
        }}
      >
        {editing !== null ? (
          <InstallmentForm
            dealId={deal.id}
            installment={editing === 'new' ? null : editing}
            onClose={() => {
              setEditing(null);
            }}
          />
        ) : null}
      </Dialog>
      <Dialog
        open={statusOf !== null}
        onOpenChange={(next) => {
          if (!next) setStatusOf(null);
        }}
      >
        {statusOf !== null ? (
          <InstallmentStatusForm
            installment={statusOf}
            onClose={() => {
              setStatusOf(null);
            }}
          />
        ) : null}
      </Dialog>
    </Card>
  );
}

function InstallmentForm({
  dealId,
  installment,
  onClose,
}: {
  dealId: string;
  installment: DealInstallmentResource | null;
  onClose: () => void;
}): JSX.Element {
  const refresh = useCrmRefresh();
  const [label, setLabel] = useState(installment?.label ?? 'Acompte à la signature');
  const [amount, setAmount] = useState(euroInputOf(installment?.amount ?? null));
  const [dueDate, setDueDate] = useState(installment?.dueDate ?? '');
  const [submitted, setSubmitted] = useState(false);
  const errors = {
    label: label.trim() === '' ? 'Indiquer un libellé.' : undefined,
    amount: euroInputError(amount, true),
  };
  const save = useMutation({
    mutationFn: () => {
      const parsed = parseEuroInput(amount) ?? '0';
      if (installment === null) {
        const body: CreateInstallmentBody = { label: label.trim(), amount: parsed, dueDate: dueDate === '' ? null : dueDate };
        return crmSend<DealInstallmentResource>('POST', `/deals/${dealId}/installments`, body);
      }
      const body: UpdateInstallmentBody = { label: label.trim(), amount: parsed, dueDate: dueDate === '' ? null : dueDate };
      return crmSend<DealInstallmentResource>('PATCH', `/installments/${installment.id}`, body);
    },
    onSuccess: () => {
      refresh();
      onClose();
    },
  });
  return (
    <DialogContent aria-describedby={undefined}>
      <DialogHead title={installment === null ? 'Nouvel acompte' : 'Modifier l’acompte'} />
      <form
        id="installment-form"
        className="contents"
        onSubmit={(event) => {
          event.preventDefault();
          setSubmitted(true);
          if (errors.label === undefined && errors.amount === undefined) {
            save.mutate();
          }
        }}
      >
        <DialogBody>
          <Field label="Libellé" required error={submitted ? errors.label : undefined}>
            <Input
              value={label}
              maxLength={200}
              onChange={(event) => {
                setLabel(event.target.value);
              }}
            />
          </Field>
          <Fields2>
            <Field label="Montant HT" required error={submitted ? errors.amount : undefined}>
              <InputGroup suffix="€">
                <Input
                  numeric
                  inputMode="decimal"
                  value={amount}
                  onChange={(event) => {
                    setAmount(event.target.value);
                  }}
                />
              </InputGroup>
            </Field>
            <Field label="Échéance">
              <DateInput
                value={dueDate}
                onChange={(value) => {
                  setDueDate(value);
                }}
              />
            </Field>
          </Fields2>
          <MutationError message={save.isError ? errorMessage(save.error, 'Enregistrement refusé.') : null} />
        </DialogBody>
      </form>
      <FormFooter form="installment-form" label="Enregistrer" busy={save.isPending} onClose={onClose} />
    </DialogContent>
  );
}

function InstallmentStatusForm({
  installment,
  onClose,
}: {
  installment: DealInstallmentResource;
  onClose: () => void;
}): JSX.Element {
  const refresh = useCrmRefresh();
  const today = parisToday();
  const initial = installment.status === 'planned' ? 'invoiced' : installment.status === 'invoiced' ? 'paid' : 'paid';
  const [status, setStatus] = useState<DealInstallmentResource['status']>(initial);
  const [invoicedOn, setInvoicedOn] = useState(installment.invoicedOn ?? today);
  const [reference, setReference] = useState(installment.reference ?? '');
  const [paidOn, setPaidOn] = useState(installment.paidOn ?? today);
  const save = useMutation({
    mutationFn: () => {
      const ref = reference.trim() === '' ? null : reference.trim();
      const body: UpdateInstallmentBody = {
        state:
          status === 'planned'
            ? { status: 'planned' }
            : status === 'invoiced'
              ? { status: 'invoiced', invoicedOn, reference: ref }
              : { status: 'paid', invoicedOn: invoicedOn === '' ? null : invoicedOn, reference: ref, paidOn },
      };
      return crmSend<DealInstallmentResource>('PATCH', `/installments/${installment.id}`, body);
    },
    onSuccess: () => {
      refresh();
      onClose();
    },
  });
  return (
    <DialogContent aria-describedby={undefined}>
      <DialogHead title="Suivi de l’acompte" description={`${installment.label}, ${eurOrDash(installment.amount)} HT`} />
      <form
        id="installment-status-form"
        className="contents"
        onSubmit={(event) => {
          event.preventDefault();
          save.mutate();
        }}
      >
        <DialogBody>
          <Field label="Statut">
            <NativeSelect
              value={status}
              onChange={(event) => {
                const value = event.target.value;
                if (value === 'planned' || value === 'invoiced' || value === 'paid') {
                  setStatus(value);
                }
              }}
            >
              {(['planned', 'invoiced', 'paid'] as const).map((value) => (
                <option key={value} value={value}>
                  {INSTALLMENT_STATUS_LABEL[value]}
                </option>
              ))}
            </NativeSelect>
          </Field>
          {status !== 'planned' ? (
            <Fields2>
              <Field label="Facturé le">
                <DateInput
                  value={invoicedOn}
                  required={status === 'invoiced'}
                  onChange={(value) => {
                    setInvoicedOn(value);
                  }}
                />
              </Field>
              <Field label="N° de facture">
                <Input
                  value={reference}
                  maxLength={100}
                  onChange={(event) => {
                    setReference(event.target.value);
                  }}
                />
              </Field>
            </Fields2>
          ) : null}
          {status === 'paid' ? (
            <Field label="Encaissé le">
              <DateInput
                required
                value={paidOn}
                onChange={(value) => {
                  setPaidOn(value);
                }}
              />
            </Field>
          ) : null}
          <MutationError message={save.isError ? errorMessage(save.error, 'Enregistrement refusé.') : null} />
        </DialogBody>
      </form>
      <FormFooter form="installment-status-form" label="Enregistrer" busy={save.isPending} onClose={onClose} />
    </DialogContent>
  );
}

// --- Propositions -----------------------------------------------------------------

/**
 * Historique des propositions (lot 21) : V1, V2… avec montants, échéancier et
 * PDF. Envoyer une proposition fait passer l'affaire en « Proposition présentée » ;
 * l'accepter reporte ses montants et ses acomptes sur l'affaire.
 */
export function ProposalsCard({ detail }: { detail: DealDetailResource }): JSX.Element {
  const refresh = useCrmRefresh();
  const [creating, setCreating] = useState(false);
  const [deciding, setDeciding] = useDialog<{ proposal: DealProposalResource; action: 'send' | 'accept' | 'decline' }>();
  const open = detail.deal.stage !== 'won' && detail.deal.stage !== 'lost';
  const documents = new Map(detail.documents.map((d) => [d.id, d]));
  const decide = useMutation({
    mutationFn: (input: { proposal: DealProposalResource; action: 'send' | 'accept' | 'decline'; date: string }) =>
      crmSend<DealProposalResource>(
        'POST',
        `/proposals/${input.proposal.id}/${input.action}`,
        input.action === 'send' ? { sentOn: input.date } : { decidedOn: input.date },
      ),
    onSuccess: () => {
      refresh();
      setDeciding(null);
    },
  });

  return (
    <Card>
      <CardHead
        title="Propositions"
        description="Chaque version envoyée reste dans l’historique"
        actions={
          open ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                setCreating(true);
              }}
            >
              <Plus aria-hidden="true" />
              Nouvelle proposition
            </Button>
          ) : undefined
        }
      />
      <CardContent>
        {detail.proposals.length === 0 ? (
          <EmptyState
            className="py-2"
            title="Aucune proposition"
            description="Saisir la proposition (montants, acomptes) et joindre son PDF."
          />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Version</TableHead>
                <TableHead kind="num">Signature</TableHead>
                <TableHead kind="num">Hébergement / mois</TableHead>
                <TableHead kind="num">IA / mois (est.)</TableHead>
                <TableHead>Statut</TableHead>
                <TableHead kind="act">
                  <span className="sr-only">Actions</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {detail.proposals.map((proposal) => {
                const document = proposal.documentId === null ? undefined : documents.get(proposal.documentId);
                const canDecide = open && (proposal.status === 'draft' || proposal.status === 'sent');
                return (
                  <TableRow key={proposal.id}>
                    <TableCell kind="name">
                      <b className="block font-medium">{proposal.title}</b>
                      <span className="block text-caption text-muted-foreground">
                        {proposal.sentOn !== null ? `Envoyée le ${formatDate(proposal.sentOn)}` : 'Non envoyée'}
                        {proposal.installments.length > 0
                          ? ` · ${proposal.installments.length} acompte${proposal.installments.length > 1 ? 's' : ''}`
                          : ''}
                      </span>
                      {document !== undefined ? (
                        <button
                          type="button"
                          className="mt-0.5 inline-flex items-center gap-1 text-caption underline underline-offset-[3px]"
                          onClick={() => {
                            void openDealDocument(document);
                          }}
                        >
                          <FileText className="size-3.5" aria-hidden="true" />
                          {document.label}
                        </button>
                      ) : null}
                    </TableCell>
                    <TableCell kind="num">{eurOrDash(proposal.setup)}</TableCell>
                    <TableCell kind="num">{eurOrDash(proposal.monthly)}</TableCell>
                    <TableCell kind="num">{eurOrDash(proposal.aiMonthly)}</TableCell>
                    <TableCell>
                      <Badge variant={PROPOSAL_BADGE[proposal.status]}>{PROPOSAL_STATUS_LABEL[proposal.status]}</Badge>
                      {proposal.decidedOn !== null && (proposal.status === 'accepted' || proposal.status === 'declined') ? (
                        <span className="ml-1 text-caption text-muted-foreground">le {formatDate(proposal.decidedOn)}</span>
                      ) : null}
                    </TableCell>
                    <TableCell kind="act">
                      {canDecide ? (
                        <div className="flex justify-end gap-1">
                          {proposal.status === 'draft' ? (
                            <Button
                              type="button"
                              variant="outline"
                              size="xs"
                              onClick={() => {
                                setDeciding({ proposal, action: 'send' });
                              }}
                            >
                              Envoyée
                            </Button>
                          ) : null}
                          <Button
                            type="button"
                            variant="outline"
                            size="xs"
                            onClick={() => {
                              setDeciding({ proposal, action: 'accept' });
                            }}
                          >
                            Acceptée
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="xs"
                            onClick={() => {
                              setDeciding({ proposal, action: 'decline' });
                            }}
                          >
                            Refusée
                          </Button>
                        </div>
                      ) : null}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </CardContent>
      <Dialog
        open={creating}
        onOpenChange={(next) => {
          if (!next) setCreating(false);
        }}
      >
        {creating ? (
          <ProposalForm
            detail={detail}
            onClose={() => {
              setCreating(false);
            }}
          />
        ) : null}
      </Dialog>
      <Dialog
        open={deciding !== null}
        onOpenChange={(next) => {
          if (!next) setDeciding(null);
        }}
      >
        {deciding !== null ? (
          <DecisionForm
            proposal={deciding.proposal}
            action={deciding.action}
            busy={decide.isPending}
            error={decide.isError ? errorMessage(decide.error, 'Enregistrement refusé.') : null}
            onConfirm={(date) => {
              decide.mutate({ ...deciding, date });
            }}
            onClose={() => {
              setDeciding(null);
            }}
          />
        ) : null}
      </Dialog>
    </Card>
  );
}

const DECISION_TEXT = {
  send: {
    title: 'Proposition envoyée',
    label: 'Envoyée le',
    note: 'La version précédente envoyée passe en « Remplacée ». Une affaire pas encore à l’étape « Proposition présentée » y passe, avec sa relance à J+5.',
  },
  accept: {
    title: 'Proposition acceptée',
    label: 'Acceptée le',
    note: 'Les montants de l’affaire et ses acomptes prévus sont remplacés par ceux de cette proposition ; les acomptes facturés ou payés restent.',
  },
  decline: {
    title: 'Proposition refusée',
    label: 'Refusée le',
    note: 'L’affaire continue : une nouvelle version peut suivre.',
  },
} as const;

function DecisionForm({
  proposal,
  action,
  busy,
  error,
  onConfirm,
  onClose,
}: {
  proposal: DealProposalResource;
  action: 'send' | 'accept' | 'decline';
  busy: boolean;
  error: string | null;
  onConfirm: (date: string) => void;
  onClose: () => void;
}): JSX.Element {
  const [date, setDate] = useState(parisToday());
  const text = DECISION_TEXT[action];
  return (
    <DialogContent aria-describedby={undefined}>
      <DialogHead title={text.title} description={proposal.title} />
      <form
        id="decision-form"
        className="contents"
        onSubmit={(event) => {
          event.preventDefault();
          onConfirm(date);
        }}
      >
        <DialogBody>
          <Field label={text.label}>
            <DateInput
              required
              value={date}
              onChange={(value) => {
                setDate(value);
              }}
            />
          </Field>
          <p className="m-0 text-caption text-muted-foreground">{text.note}</p>
          <MutationError message={error} />
        </DialogBody>
      </form>
      <FormFooter form="decision-form" label="Confirmer" busy={busy} onClose={onClose} />
    </DialogContent>
  );
}

interface PlanLine {
  label: string;
  amount: string;
  dueDate: string;
}

function ProposalForm({ detail, onClose }: { detail: DealDetailResource; onClose: () => void }): JSX.Element {
  const refresh = useCrmRefresh();
  const { deal } = detail;
  const pdfs = detail.documents.filter((d) => d.kind === 'file' || d.category === 'proposal');
  const [title, setTitle] = useState('');
  const [setup, setSetup] = useState(euroInputOf(deal.setup));
  const [monthly, setMonthly] = useState(euroInputOf(deal.monthly));
  const [aiMonthly, setAiMonthly] = useState(euroInputOf(deal.aiMonthly));
  const [plan, setPlan] = useState<PlanLine[]>([]);
  const [documentId, setDocumentId] = useState('');
  const [note, setNote] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const planErrors = plan.map((line) =>
    line.label.trim() === '' ? 'Libellé obligatoire.' : euroInputError(line.amount, true),
  );
  const errors = {
    setup: euroInputError(setup),
    monthly: euroInputError(monthly),
    aiMonthly: euroInputError(aiMonthly),
  };
  const valid =
    Object.values(errors).every((e) => e === undefined) && planErrors.every((e) => e === undefined);
  const shown = (error: string | undefined): string | undefined => (submitted ? error : undefined);

  /** Split the signature: 30 % at signature, 70 % on delivery. */
  const splitPlan = (): void => {
    const total = Number(parseEuroInput(setup) ?? '0');
    if (total > 0) {
      const first = Math.round(total * 30) / 100;
      setPlan([
        { label: 'Acompte à la signature (30 %)', amount: euroInputOf(first.toFixed(2)), dueDate: '' },
        { label: 'Solde à la recette (70 %)', amount: euroInputOf((total - first).toFixed(2)), dueDate: '' },
      ]);
    }
  };

  const save = useMutation({
    mutationFn: () => {
      const installments: PlannedInstallmentBody[] = plan.map((line) => ({
        label: line.label.trim(),
        amount: parseEuroInput(line.amount) ?? '0',
        dueDate: line.dueDate === '' ? null : line.dueDate,
      }));
      const body: CreateProposalBody = {
        title: title.trim() === '' ? null : title.trim(),
        setup: parseEuroInput(setup) ?? null,
        monthly: parseEuroInput(monthly) ?? null,
        aiMonthly: parseEuroInput(aiMonthly) ?? null,
        installments,
        documentId: documentId === '' ? null : documentId,
        note: note.trim() === '' ? null : note.trim(),
      };
      return crmSend<DealProposalResource>('POST', `/deals/${deal.id}/proposals`, body);
    },
    onSuccess: () => {
      refresh();
      onClose();
    },
  });

  const next = detail.proposals.reduce((max, p) => Math.max(max, p.version), 0) + 1;
  return (
    <DialogContent width={640} aria-describedby={undefined}>
      <DialogHead title={`Nouvelle proposition (V${next})`} description={`${detail.company.name}, ${deal.title}`} />
      <form
        id="proposal-form"
        className="contents"
        onSubmit={(event) => {
          event.preventDefault();
          setSubmitted(true);
          if (valid) {
            save.mutate();
          }
        }}
      >
        <DialogBody>
          <Field label="Intitulé">
            <Input
              value={title}
              maxLength={200}
              placeholder={`Proposition V${next}`}
              onChange={(event) => {
                setTitle(event.target.value);
              }}
            />
          </Field>
          <Fieldset title="Montants HT">
            <Fields2>
              <Field label="Signature (mise en place)" error={shown(errors.setup)}>
                <InputGroup suffix="€">
                  <Input
                    numeric
                    inputMode="decimal"
                    value={setup}
                    onChange={(event) => {
                      setSetup(event.target.value);
                    }}
                  />
                </InputGroup>
              </Field>
              <Field label="Hébergement mensuel" error={shown(errors.monthly)}>
                <InputGroup suffix="€ / mois">
                  <Input
                    numeric
                    inputMode="decimal"
                    value={monthly}
                    onChange={(event) => {
                      setMonthly(event.target.value);
                    }}
                  />
                </InputGroup>
              </Field>
            </Fields2>
            <Field label="Frais d’IA estimés" error={shown(errors.aiMonthly)}>
              <InputGroup suffix="€ / mois">
                <Input
                  numeric
                  inputMode="decimal"
                  placeholder="Facturés à la consommation"
                  value={aiMonthly}
                  onChange={(event) => {
                    setAiMonthly(event.target.value);
                  }}
                />
              </InputGroup>
            </Field>
          </Fieldset>
          <Fieldset title="Acomptes de la signature">
            {plan.map((line, index) => (
              <div key={index} className="grid grid-cols-[1fr_130px_150px_auto] items-end gap-2">
                <Field label="Libellé" hideLabel={index > 0} error={shown(planErrors[index])}>
                  <Input
                    value={line.label}
                    maxLength={200}
                    onChange={(event) => {
                      setPlan(plan.map((l, i) => (i === index ? { ...l, label: event.target.value } : l)));
                    }}
                  />
                </Field>
                <Field label="Montant" hideLabel={index > 0}>
                  <InputGroup suffix="€">
                    <Input
                      numeric
                      inputMode="decimal"
                      value={line.amount}
                      onChange={(event) => {
                        setPlan(plan.map((l, i) => (i === index ? { ...l, amount: event.target.value } : l)));
                      }}
                    />
                  </InputGroup>
                </Field>
                <Field label="Échéance" hideLabel={index > 0}>
                  <DateInput
                    value={line.dueDate}
                    onChange={(value) => {
                      setPlan(plan.map((l, i) => (i === index ? { ...l, dueDate: value } : l)));
                    }}
                  />
                </Field>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  aria-label="Retirer l’acompte"
                  onClick={() => {
                    setPlan(plan.filter((_, i) => i !== index));
                  }}
                >
                  <Trash2 aria-hidden="true" />
                </Button>
              </div>
            ))}
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  setPlan([...plan, { label: plan.length === 0 ? 'Acompte à la signature' : 'Solde', amount: '', dueDate: '' }]);
                }}
              >
                <Plus aria-hidden="true" />
                Ajouter un acompte
              </Button>
              <Button type="button" variant="ghost" size="sm" onClick={splitPlan}>
                30 % / 70 %
              </Button>
            </div>
          </Fieldset>
          <Field label="PDF de la proposition">
            <NativeSelect
              value={documentId}
              onChange={(event) => {
                setDocumentId(event.target.value);
              }}
            >
              <option value="">Aucun (déposer le PDF dans Documents d’abord)</option>
              {pdfs.map((document) => (
                <option key={document.id} value={document.id}>
                  {document.label}
                </option>
              ))}
            </NativeSelect>
          </Field>
          <Field label="Note">
            <Textarea
              rows={2}
              maxLength={2000}
              value={note}
              onChange={(event) => {
                setNote(event.target.value);
              }}
            />
          </Field>
          <MutationError message={save.isError ? errorMessage(save.error, 'Enregistrement refusé.') : null} />
        </DialogBody>
      </form>
      <FormFooter form="proposal-form" label="Créer le brouillon" busy={save.isPending} onClose={onClose} />
    </DialogContent>
  );
}

function FormFooter({
  form,
  label,
  busy,
  onClose,
}: {
  form: string;
  label: string;
  busy: boolean;
  onClose: () => void;
}): JSX.Element {
  return (
    <DialogFooter>
      <Button type="button" variant="outline" onClick={onClose}>
        Annuler
      </Button>
      <Button type="submit" form={form} size="lg" disabled={busy}>
        {label}
      </Button>
    </DialogFooter>
  );
}
